import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { MAX_MENU_IMAGE_BYTES } from '@kortex/shared';
import { api } from '../lib/api';
import { useToast } from './ui';

/** Longest edge of a stored picture. Plenty for a phone-sized tile. */
const MAX_EDGE = 800;
const JPEG_QUALITY = 0.82;

/**
 * Shrinks whatever was picked before it is uploaded.
 *
 * A photograph straight off a phone is four megabytes; the column takes 400 KB.
 * Doing this in the browser means the big file never crosses the network, the
 * server never has to decode anything a stranger sent it, and the hotel's data
 * bill is a hundredth of what it would be. The output is always JPEG - the one
 * format that is both universally supported and small for photographs.
 */
export async function toThumbnail(file: File): Promise<{ mime: string; data: string }> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser cannot resize images');
  // A transparent PNG would otherwise come out black once it is flattened.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  return { mime: 'image/jpeg', data: dataUrl.slice(dataUrl.indexOf(',') + 1) };
}

/**
 * Picking, shrinking and storing one picture.
 *
 * Shared by dish thumbnails and room photographs: same size limit, same
 * shrinking, same refusal of anything that is not a raster image. Only the
 * endpoint and the words around it differ.
 */
export function ImagePicker({
  endpoint,
  imageUrl,
  alt,
  canEdit,
  invalidateKeys,
  size = 'lg',
  uploadLabel = 'Upload a photo',
}: {
  /** Where PUT stores it and DELETE removes it. */
  endpoint: string;
  imageUrl: string | null;
  alt: string;
  canEdit: boolean;
  invalidateKeys: unknown[][];
  size?: 'lg' | 'sm';
  uploadLabel?: string;
}) {
  const queryClient = useQueryClient();
  const { push, fail } = useToast();
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const invalidate = () => {
    for (const key of invalidateKeys) void queryClient.invalidateQueries({ queryKey: key });
  };

  const upload = useMutation({
    mutationFn: (payload: { mime: string; data: string }) => api.put(endpoint, payload),
    onSuccess: () => {
      push('Picture saved', 'ok');
      invalidate();
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: () => api.delete(endpoint),
    onSuccess: () => {
      push('Picture removed', 'ok');
      invalidate();
    },
    onError: fail,
  });

  const choose = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const thumbnail = await toThumbnail(file);
      if (thumbnail.data.length * 0.75 > MAX_MENU_IMAGE_BYTES) {
        throw new Error('That picture is still too large — try a smaller one.');
      }
      await upload.mutateAsync(thumbnail);
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  return (
    <div className="row gap-16 wrap" style={{ alignItems: 'flex-start' }}>
      {imageUrl ? (
        <img className={`menu-thumb ${size}`} src={imageUrl} alt={alt} />
      ) : (
        <div className={`menu-thumb ${size} blank`} aria-hidden="true">
          ▨
        </div>
      )}

      {canEdit ? (
        <div className="col gap-6">
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => void choose(e.target.files?.[0])}
          />
          <div className="row gap-6">
            <button
              className="btn primary sm"
              disabled={busy || upload.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {busy || upload.isPending ? 'Uploading…' : imageUrl ? 'Replace' : uploadLabel}
            </button>
            {imageUrl ? (
              <button
                className="btn sm"
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
              >
                Remove
              </button>
            ) : null}
          </div>
          <p className="small muted" style={{ maxWidth: '38ch' }}>
            JPEG, PNG or WebP. Whatever you pick is shrunk here before it is uploaded, so a photo
            straight off your phone is fine.
          </p>
        </div>
      ) : null}
    </div>
  );
}
